import React from 'react'
import { Modal, Button, Card, Descriptions, Tag, Space, Spin } from 'antd'
import { CheckOutlined, CloseOutlined, FileTextOutlined } from '@ant-design/icons'
import moment from 'moment'
import { maskCurrency, maskCpf } from '../../utils/functions'

const SaqueDetailsModal = (props) => {
    const { 
        visible, 
        saque, 
        loading, 
        handleOk, 
        handleCancel, 
        handleApprove, 
        handleReject, 
        handleFinalize,
        isSubmit 
    } = props
    
    // Status tag with appropriate color
    const renderStatus = (status) => {
        // Convert numeric status to text
        let statusText = status;
        let statusNumber = status;
        
        if (typeof status === 'number' || !isNaN(Number(status))) {
            statusNumber = Number(status);
            switch (statusNumber) {
                case 0:
                    statusText = 'Pendente';
                    return <Tag color="orange">Pendente</Tag>;
                case 1:
                    statusText = 'Aprovado';
                    return <Tag color="blue">Aprovado</Tag>;
                case 2:
                case 4:
                    statusText = 'Rejeitado';
                    return <Tag color="red">Rejeitado</Tag>;
                case 3:
                    statusText = 'Finalizado';
                    return <Tag color="green">Finalizado</Tag>;
                default:
                    statusText = `Status ${status}`;
                    return <Tag>{statusText}</Tag>;
            }
        }
        
        if (status === 'pending') {
            return <Tag color="orange">Pendente</Tag>
        } else if (status === 'approved') {
            return <Tag color="blue">Aprovado</Tag>
        } else if (status === 'rejected') {
            return <Tag color="red">Rejeitado</Tag>
        } else if (status === 'finalized') {
            return <Tag color="green">Finalizado</Tag>
        }
        return <Tag>{status}</Tag>
    }
    
    // Payment method details
    const renderPaymentDetails = () => {
        if (!saque) return null
        
        // Verificar a estrutura de dados para PIX
        if (saque.formaPagamento === 'pix' || saque.paymentMethod === 'pix' || saque.pix || saque.paymentInfo) {
            // Tentar todas as possíveis estruturas de dados PIX
            let pixKeyType = '';
            let pixKey = '';
            
            // Estrutura principal
            if (saque.pix && typeof saque.pix === 'object') {
                pixKeyType = saque.pix.tipoChave || saque.pix.pixKeyType || '';
                pixKey = saque.pix.chavePix || saque.pix.pixKey || '';
            } 
            
            // Estrutura alternativa vista na imagem
            if (saque.paymentInfo && typeof saque.paymentInfo === 'object') {
                pixKeyType = saque.paymentInfo.pixKeyType || '';
                pixKey = saque.paymentInfo.pixKey || '';
            }
            
            // Verificar estrutura de JSON diretamente no objeto
            if (saque.paymentMethod === 'pix' && saque.pixKeyType && saque.pixKey) {
                pixKeyType = saque.pixKeyType;
                pixKey = saque.pixKey;
            }
            
            return (
                <Card title="Dados de PIX" className="saque-detail-card">
                    <div className="payment-method-pix">
                        <Descriptions bordered column={1}>
                            <Descriptions.Item label="Tipo de Chave">{pixKeyType || "Não informado"}</Descriptions.Item>
                            <Descriptions.Item label="Chave PIX">
                                <span className="payment-key">{pixKey || "Não informada"}</span>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                </Card>
            )
        } else if (saque.formaPagamento === 'bank' || saque.paymentMethod === 'bank' || saque.dadosBancarios) {
            // Tentar todas as possíveis estruturas de dados bancários
            let dados = {};
            
            if (saque.dadosBancarios && typeof saque.dadosBancarios === 'object') {
                dados = saque.dadosBancarios;
            } else if (saque.paymentInfo && typeof saque.paymentInfo === 'object') {
                dados = saque.paymentInfo;
            }
            
            return (
                <Card title="Dados Bancários" className="saque-detail-card">
                    <div className="payment-method-bank">
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Banco">{dados.banco || dados.bankName || "Não informado"}</Descriptions.Item>
                            <Descriptions.Item label="Agência">{dados.agencia || dados.agency || "Não informada"}</Descriptions.Item>
                            <Descriptions.Item label="Conta">{dados.conta || dados.account || "Não informada"}</Descriptions.Item>
                            <Descriptions.Item label="Tipo de Conta">{dados.tipoConta || dados.accountType || "Não informado"}</Descriptions.Item>
                            <Descriptions.Item label="CPF/CNPJ" span={2}>{dados.cpfCnpj || dados.document || "Não informado"}</Descriptions.Item>
                        </Descriptions>
                    </div>
                </Card>
            )
        } else {
            // Fallback if forma de pagamento is not properly set
            return (
                <Card title="Dados de Pagamento" className="saque-detail-card">
                    <div style={{ padding: '16px', textAlign: 'center' }}>
                        <p>Dados de pagamento não disponíveis ou não informados.</p>
                        <pre style={{ textAlign: 'left', fontSize: '12px', background: '#f8f8f8', padding: '8px', overflow: 'auto' }}>
                            {JSON.stringify(saque, null, 2)}
                        </pre>
                    </div>
                </Card>
            )
        }
    }
    
    // Receipt file info
    const renderComprovante = () => {
        if (!saque) return null
        
        // Check for finalized status (both as string and as number 3)
        if (saque.status !== 'finalized' && saque.status !== 3) return null
        
        return (
            <Card title="Comprovante de Transferência" className="saque-detail-card">
                <div className="payment-method-pix" style={{ textAlign: 'center' }}>
                    {saque.comprovante ? (
                        <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            onClick={() => window.open(saque.comprovante, '_blank')}
                            style={{ margin: '10px 0' }}
                        >
                            Visualizar Comprovante
                        </Button>
                    ) : (
                        <p>Nenhum comprovante disponível</p>
                    )}
                </div>
            </Card>
        )
    }
    
    // Action buttons based on status
    const renderActionButtons = () => {
        if (!saque) return null
        
        // Debug logging
        console.log("Saque details:", saque);
        
        // Handle both string and numeric status values
        const status = saque.status
        const isPending = status === 'pending' || status === 0
        const isApproved = status === 'approved' || status === 1
        
        // Debug logging for podeAprovar
        console.log("podeAprovar:", saque.podeAprovar, "type:", typeof saque.podeAprovar);
        console.log("saldoDisponivel:", saque.saldoDisponivel, "type:", typeof saque.saldoDisponivel);
        
        if (isPending && saque.podeAprovar === true) {
            return (
                <Space>
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => handleApprove(saque.id)}
                        loading={isSubmit}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Aprovar
                    </Button>
                    <Button
                        type="primary"
                        icon={<CloseOutlined />}
                        onClick={() => handleReject(saque.id)}
                        loading={isSubmit}
                        danger
                    >
                        Rejeitar
                    </Button>
                </Space>
            )
        } else if (isApproved) {
            return (
                <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => handleFinalize(saque.id)}
                    loading={isSubmit}
                >
                    Finalizar Saque
                </Button>
            )
        }
        
        return null
    }
    
    // Format date with check for null/undefined
    const formatDate = (date) => {
        return date ? moment(date).format('DD/MM/YYYY HH:mm') : "Não realizado";
    };
    
    return (
        <Modal
            title="Detalhes da Solicitação de Saque"
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            width={700}
            footer={[
                <Button key="close" onClick={handleCancel}>
                    Fechar
                </Button>,
                renderActionButtons()
            ]}
        >
            {loading || !saque ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div className="modal-detail">
                    <Card title="Informações Gerais" className="saque-detail-card">
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="ID">{saque.id}</Descriptions.Item>
                            <Descriptions.Item label="Contrato">{saque.idContrato}</Descriptions.Item>
                            <Descriptions.Item label="Status">{renderStatus(saque.status)}</Descriptions.Item>
                            <Descriptions.Item label="Valor">R$ {maskCurrency(saque.valor)}</Descriptions.Item>
                            <Descriptions.Item label="Tipo" span={2}>
                                {saque.tipo || saque.saqueType || "Padrão"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Saldo Disponível" span={2}>
                                <pre style={{ display: 'none' }}>
                                    {JSON.stringify({
                                        saldoDisponivel: saque.saldoDisponivel,
                                        typeof: typeof saque.saldoDisponivel
                                    }, null, 2)}
                                </pre>
                                {`R$ ${maskCurrency(Number(saque.saldoDisponivel) || saque.valor || 0)}`}
                            </Descriptions.Item>
                            <Descriptions.Item label="Valor Solicitado" span={2}>
                                {saque.valorSolicitado ? `R$ ${maskCurrency(saque.valorSolicitado)}` : `R$ ${maskCurrency(saque.valor)}`}
                            </Descriptions.Item>
                            <Descriptions.Item label="Forma de Pagamento">
                                {saque.formaPagamento === 'pix' || saque.paymentMethod === 'pix' ? 'PIX' : 'Dados Bancários'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Permissão">
                                <pre style={{ display: 'none' }}>
                                    {JSON.stringify({
                                        podeAprovar: saque.podeAprovar,
                                        typeof: typeof saque.podeAprovar,
                                        truthyCheck: !!saque.podeAprovar
                                    }, null, 2)}
                                </pre>
                                {saque.podeAprovar === true ? (
                                    <Tag color="green">Pode Aprovar</Tag>
                                ) : (
                                    <Tag color="red">Não Pode Aprovar</Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    
                    <Card title="Datas do Processo" className="saque-detail-card">
                        <Descriptions bordered column={1}>
                            <Descriptions.Item label="Data de Solicitação">
                                {formatDate(saque.dataSolicitacao || saque.dataPedido || saque.createdAt || saque.updatedAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Data de Aprovação">
                                {formatDate(saque.dataAprovacao)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Data de Cotização">
                                {formatDate(saque.dataCotizacao)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Data de Transferência">
                                {formatDate(saque.dataTransferencia)}
                            </Descriptions.Item>
                            {saque.cotacao && (
                                <Descriptions.Item label="Cotação">
                                    {saque.cotacao}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                    
                    <Card title="Dados do Usuário" className="saque-detail-card">
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Nome">
                                {saque.usuario?.nome || saque.usuario?.name || saque.userName || "Não informado"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">
                                {saque.usuario?.email || saque.userEmail || "Não informado"}
                            </Descriptions.Item>
                            <Descriptions.Item label="CPF">
                                {maskCpf(saque.usuario?.cpf || saque.userDocument || saque.usuarioCpf || "")}
                            </Descriptions.Item>
                            <Descriptions.Item label="Telefone">
                                {saque.usuario?.telefone || saque.usuario?.phone || saque.userPhone || "Não informado"}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    
                    {renderPaymentDetails()}
                    
                    {saque.status === 'rejected' || saque.status === 2 || saque.status === 4 ? (
                        <Card title="Motivo da Rejeição" className="saque-detail-card">
                            <p>{saque.motivoNegado || saque.motivo || "Sem motivo informado"}</p>
                        </Card>
                    ) : null}
                    
                    {renderComprovante()}
                </div>
            )}
        </Modal>
    )
}

export default SaqueDetailsModal 